"use strict";

import _defineProperty from "@babel/runtime-corejs3/helpers/defineProperty";
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/esnext.set.add-all.js";
import "core-js/modules/esnext.set.delete-all.js";
import "core-js/modules/esnext.set.difference.js";
import "core-js/modules/esnext.set.every.js";
import "core-js/modules/esnext.set.filter.js";
import "core-js/modules/esnext.set.find.js";
import "core-js/modules/esnext.set.intersection.js";
import "core-js/modules/esnext.set.is-disjoint-from.js";
import "core-js/modules/esnext.set.is-subset-of.js";
import "core-js/modules/esnext.set.is-superset-of.js";
import "core-js/modules/esnext.set.join.js";
import "core-js/modules/esnext.set.map.js";
import "core-js/modules/esnext.set.reduce.js";
import "core-js/modules/esnext.set.some.js";
import "core-js/modules/esnext.set.symmetric-difference.js";
import "core-js/modules/esnext.set.union.js";
import "core-js/modules/web.dom-collections.iterator.js";
import { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LANGUAGE } from '../lib/languages';

/**
 * Singleton event listener manager for language changes
 * This ensures we only have one set of event listeners across all MFEs
 * instead of multiple listeners per component
 */
let LanguageChangeManager = /*#__PURE__*/function () {
  function LanguageChangeManager() {
    _defineProperty(this, "listeners", new Set());
    _defineProperty(this, "isListening", false);
    _defineProperty(this, "i18nListenerSetup", false);
    // Get initial language from localStorage or default
    this.currentLanguage = typeof window !== 'undefined' ? window.localStorage.getItem('i18nextLng') || DEFAULT_LANGUAGE : DEFAULT_LANGUAGE;
    this.setupListeners();
  }
  LanguageChangeManager.getInstance = function getInstance() {
    if (!LanguageChangeManager.instance) {
      LanguageChangeManager.instance = new LanguageChangeManager();
    }
    return LanguageChangeManager.instance;
  };
  var _proto = LanguageChangeManager.prototype;
  _proto.setupListeners = function setupListeners() {
    if (this.isListening) return;
    this.isListening = true;

    // Listen to custom language-change events from other MFEs
    const handleCustomEvent = event => {
      var _customEvent$detail;
      const customEvent = event;
      const newLang = (_customEvent$detail = customEvent.detail) === null || _customEvent$detail === void 0 ? void 0 : _customEvent$detail.language;
      if (newLang && newLang !== this.currentLanguage) {
        this.currentLanguage = newLang;
        this.notifyListeners();
      }
    };

    // Listen to storage events for cross-tab synchronization
    const handleStorageEvent = event => {
      if (event.key === 'i18nextLng' && event.newValue) {
        const newLang = event.newValue;
        if (newLang !== this.currentLanguage) {
          this.currentLanguage = newLang;
          this.notifyListeners();
        }
      }
    };
    window.addEventListener('language-changed', handleCustomEvent);
    window.addEventListener('language-change', handleCustomEvent); // Also listen to both variants
    window.addEventListener('storage', handleStorageEvent);

    // Log in development
    if (__DEBUG__) {
      console.log('%c🎯 [i18n:singleton] Language change manager initialized', 'color: #FF5722; font-weight: bold');
    }
  }

  // New method to register i18n instance
  ;
  _proto.registerI18n = function registerI18n(i18n) {
    if (this.i18nListenerSetup || !i18n) return;
    this.i18nListenerSetup = true;

    // Update current language from i18n
    if (i18n.language) {
      this.currentLanguage = i18n.language;
    }

    // Listen to i18next language changes
    i18n.on('languageChanged', lng => {
      if (lng !== this.currentLanguage) {
        this.currentLanguage = lng;
        this.notifyListeners();
      }
    });
    if (__DEBUG__) {
      console.log('%c🔗 [i18n:singleton] i18next instance registered', 'color: #2196F3; font-weight: bold');
    }
  };
  _proto.subscribe = function subscribe(callback) {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  };
  _proto.notifyListeners = function notifyListeners() {
    for (const listener of this.listeners) {
      listener();
    }
  };
  _proto.getCurrentLanguage = function getCurrentLanguage() {
    return this.currentLanguage;
  }

  // Method to get statistics for monitoring
  ;
  _proto.getStats = function getStats() {
    return {
      listenersCount: this.listeners.size,
      currentLanguage: this.currentLanguage,
      i18nRegistered: this.i18nListenerSetup
    };
  };
  return LanguageChangeManager;
}();
/**
 * Custom hook for MFE translations that automatically handles language synchronization
 *
 * This hook combines useTranslation with automatic language sync, ensuring that
 * components re-render when the language changes. It uses a singleton event listener
 * pattern for optimal performance across multiple MFEs.
 *
 * @param ns - Optional namespace(s) for translations (type-safe when using Namespace type)
 * @param options - Optional react-i18next options
 * @returns The same return type as useTranslation
 *
 * @example
 * ```tsx
 * import { useMfeTranslation } from '@autoguru/i18n';
 * import { Namespaces } from '@autoguru/i18n-namespaces';
 *
 * function MyComponent() {
 *   const { t } = useMfeTranslation(Namespaces.BookingTasks);
 *   return <div>{t('menu.home')}</div>;
 * }
 * ```
 */
// Type-safe overload for centralized namespaces
// Backward compatibility overload for string literals
// Implementation
export function useMfeTranslation(ns, options) {
  const translationResult = useTranslation(ns, options);
  const [, forceUpdate] = useReducer(x => x + 1, 0);
  useEffect(() => {
    const manager = LanguageChangeManager.getInstance();

    // Register i18n instance with singleton manager (only once per i18n instance)
    manager.registerI18n(translationResult.i18n);

    // Subscribe to language changes from singleton manager only
    const unsubscribe = manager.subscribe(() => {
      // Force component re-render without emitting events
      forceUpdate();
    });
    return unsubscribe;
  }, [translationResult.i18n]); // Keep dependency to ensure registration

  return translationResult;
}

/**
 * Hook to get the current language with automatic updates
 * Uses the singleton manager for consistent language tracking
 */
export function useMfeCurrentLanguage() {
  const manager = LanguageChangeManager.getInstance();
  const [, forceUpdate] = useReducer(x => x + 1, 0);
  useEffect(() => {
    const unsubscribe = manager.subscribe(() => {
      forceUpdate();
    });
    return unsubscribe;
  }, []);
  return manager.getCurrentLanguage();
}

/**
 * Utility function to get singleton manager statistics (for debugging)
 * Exposes information about the number of listeners and current state
 */
export function getLanguageManagerStats() {
  const manager = LanguageChangeManager.getInstance();
  return manager.getStats();
}

// Expose to window in development for debugging
if (__DEBUG__ && typeof window !== 'undefined') {
  window.__languageManagerStats = getLanguageManagerStats;
}